import { add } from 'date-fns';
import { jwtDecode, JwtPayload } from "jwt-decode";
import { setAxiosToken } from 'services/api';
import { signalRService } from './signalr';

const TOKEN_EXPIRES_BUFFER = 2; // in minutes

interface TokenPayload extends JwtPayload {
    scopes?: string;
}

export function isTokenAuthenticated(token: TokenPayload | null) {
    if (!token) return false;
    return !isTokenExpired(token, TOKEN_EXPIRES_BUFFER);
}

export function loadTokenFromStorage() {
    try {
        return localStorage.getItem('token');
    } catch (e) {
        console.error('Failed to load token from storage', e);
        return null;
    }
}

function loadAndDecodeToken() {
    return decodeToken(loadTokenFromStorage()!);
}

export function saveTokenToStorage(token: string) {
    localStorage.setItem('token', token);
}

export function removeTokenFromStorage() {
    localStorage.removeItem('token');
}

type TokenStatus = 'unauthenticated' | 'needs-2fa' | 'authenticated';

export function getTokenStatus(token: JwtPayload | null): {
    status: TokenStatus;
    willExpireSoon: boolean;
} {
    if (!token)
        return {
            status: 'unauthenticated',
            willExpireSoon: false,
        }

    const expiration = getTokenExpirationInfo(token);
    const needsTwoFactor = checkNeeds2FA(token);
    return {
        status: expiration.isExpired ? 'unauthenticated' : needsTwoFactor ? 'needs-2fa' : 'authenticated',
        willExpireSoon: expiration.willExpireSoon
    }
}


// Helper function to check if token is expired or about to expire
function isTokenExpired(token: JwtPayload | null, bufferMinutes = 2): boolean {
    if (!token) return true;

    try {
        const currentTime = Math.floor(Date.now() / 1000);

        // Check if current time + buffer is greater than expiration time
        return currentTime + (bufferMinutes * 60) >= token.exp!;
    } catch (error) {
        console.error('Error decoding token:', error);
        return true;
    }
}

function checkNeeds2FA(token: TokenPayload) {
    return token["scopes"] != "CUST_FULL";
}

function getTokenExpirationInfo(token: JwtPayload) {
    var expirationDate = new Date(token.exp! * 1000);
    var issueDate = new Date(token.iat! * 1000);
    var now = new Date();

    return {
        isExpired: now > expirationDate,
        willExpireSoon: now > add(issueDate, { days: 1 }),
        expirationDate: expirationDate,
        issueDate: issueDate,
    };
}

// Helper to decode JWT payload
export function decodeToken(token: string | null) {
    if (!token)
        return null;

    try {
        return jwtDecode<TokenPayload>(token);
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
}

export function setToken(token?: string) {
    setAxiosToken(token);
    signalRService.setAuthenticationToken(token);
}